import { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePageTitle } from '@/hooks/usePageTitle';
import { callsApi } from '@/lib/api/calls.api';
import { campaignsApi } from '@/lib/api/campaigns.api';
import { leadsApi } from '@/lib/api/leads.api';
import { getEnv } from '@/lib/env';

interface DashboardStats {
  activeCampaigns: number | null;
  completedCalls: number | null;
  availabilityCaptured: number | null;
  leadsGenerated: number | null;
  totalAvailabilityHours: number | null;
  availabilitySource: 'tool' | 'review' | null;
}

function formatStat(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString();
}

interface StatCard {
  title: string;
  value: string;
  subtext: string;
  company?: string;
  onClick?: () => void;
}

const FLOW_CARDS = [
  {
    title: 'Campaign Management',
    description: 'Create campaigns with contacts, schedule run dates, and configure call windows.',
  },
  {
    title: 'AI Voice Agents',
    description:
      'Gemini and OpenAI-powered agents conduct natural conversations to capture availability & lead.',
  },
  {
    title: 'Smart Retry Logic',
    description:
      'Configurable retry attempts with backoff strategies for unanswered or failed calls.',
  },
];

const QUICK_ACTIONS = [
  {
    title: 'Create Campaign',
    description: 'Set up a new outbound calling campaign with contacts.',
    buttonLabel: 'New Campaign',
    href: '/campaigns/new',
  },
  {
    title: 'Manage Configurations',
    description: 'Define AI prompts, voice settings, and call windows.',
    buttonLabel: 'View Configs',
    href: '/configurations',
  },
  {
    title: 'Review Call Logs',
    description: 'Filter by outcome, campaign, or date range.',
    buttonLabel: 'View Calls',
    href: '/calls',
  },
  {
    title: 'Browse Leads',
    description: 'Review captured availability and customer data.',
    buttonLabel: 'View Leads',
    href: '/leads',
    company: 'V12',
  },
];

export const HomePage = () => {
  usePageTitle();
  const [stats, setStats] = useState<DashboardStats>({
    activeCampaigns: null,
    completedCalls: null,
    availabilityCaptured: null,
    leadsGenerated: null,
    totalAvailabilityHours: null,
    availabilitySource: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const [activeCampaigns, completedCalls, leads, availabilityHours] = await Promise.all([
          campaignsApi.getCampaigns({ status: 'ACTIVE', limit: 1 }),
          callsApi.getCalls({ status: 'COMPLETED', limit: 1 }),
          leadsApi.getLeads({ limit: 1 }),
          callsApi.getAvailabilityHoursStats(),
        ]);

        setStats({
          activeCampaigns: activeCampaigns.pagination.total,
          completedCalls: completedCalls.pagination.total,
          // Both availability tiles come from the same call so they can never
          // disagree — and when the platform is CRM-sourced they count only
          // shifts that actually reached the booking system.
          availabilityCaptured: availabilityHours.totalCalls,
          leadsGenerated: leads.pagination.total,
          totalAvailabilityHours: availabilityHours.totalHours,
          availabilitySource: availabilityHours.source,
        });
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchStats();
  }, []);

  const statCards: StatCard[] = [
    {
      title: 'Active Campaigns',
      value: formatStat(stats.activeCampaigns),
      subtext: 'Currently running',
    },
    {
      title: 'Calls Completed',
      value: formatStat(stats.completedCalls),
      subtext: 'Total outbound calls',
    },
    {
      title: 'Availability Captured',
      value: formatStat(stats.availabilityCaptured),
      subtext:
        stats.availabilitySource === 'tool'
          ? 'Calls saved to the booking system'
          : 'Successful data captures',
      company: 'NEW_CROSS',
    },
    {
      title: 'Hours Captured',
      value:
        stats.totalAvailabilityHours !== null
          ? `${formatStat(stats.totalAvailabilityHours)}h`
          : '—',
      subtext:
        stats.availabilitySource === 'tool'
          ? 'Hours saved to the booking system'
          : 'Total availability hours',
      company: 'NEW_CROSS',
    },
    {
      title: 'Leads Generated',
      value: formatStat(stats.leadsGenerated),
      subtext: 'From AI conversations',
      company: 'V12',
    },
  ];

  return (
    <div className="space-y-10">
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Command Center</p>
        <h1 className="text-4xl font-bold tracking-tight">Ai VoiceOps platform</h1>
        <p className="text-muted-foreground max-w-3xl">
          Create campaigns, configure AI voice agents, and automate outbound calls to capture
          availability and lead data through natural conversations.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {FLOW_CARDS.map((card) => (
          <Card key={card.title}>
            <CardHeader>
              <CardTitle>{card.title}</CardTitle>
              <CardDescription>{card.description}</CardDescription>
            </CardHeader>
          </Card>
        ))}
        <Card className="bg-primary text-primary-foreground dark:bg-primary dark:text-white">
          <CardHeader>
            <CardTitle>Recording & Transcripts</CardTitle>
            <CardDescription className="text-primary-foreground/80 dark:text-white/80">
              Every call is recorded with full AI-generated transcripts, outcomes, and captured data
              for compliance and analytics.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {statCards.map((stat) =>
          stat.company && stat.company !== getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') ? null : (
            <Card
              key={stat.title}
              className={`border-dashed${stat.onClick ? 'hover:bg-muted/50 cursor-pointer transition-colors' : ''}`}
              onClick={stat.onClick}
            >
              <CardHeader className="pb-2">
                <CardDescription>{stat.title}</CardDescription>
                {isLoading ? (
                  <Skeleton className="h-9 w-20" />
                ) : (
                  <CardTitle className="text-3xl">{stat.value}</CardTitle>
                )}
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">{stat.subtext}</p>
              </CardContent>
            </Card>
          ),
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {QUICK_ACTIONS.map((action) =>
          action.company &&
          action.company !== getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') ? null : (
            <Card key={action.title} className="flex flex-col">
              <CardHeader className="flex-1 pb-2">
                <CardTitle className="text-xl">{action.title}</CardTitle>
                <CardDescription>{action.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" asChild>
                  <Link to={action.href}>{action.buttonLabel}</Link>
                </Button>
              </CardContent>
            </Card>
          ),
        )}
      </div>
    </div>
  );
};
